import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, RefreshCw, Mail, MapPin, Tag, CheckCircle2, Clock, Users, TrendingUp } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

/**
 * /platform/intakes — Platform-admin view of trial intakes with UTM data.
 * No PHI / scenario content here — just signup/lead metadata.
 */
export default function PlatformIntakes() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    items: [], total_matching: 0, overall_total: 0, overall_linked: 0, by_utm_source: [],
  });
  const [search, setSearch] = useState("");
  const [utmSourceFilter, setUtmSourceFilter] = useState("");
  const [linkedFilter, setLinkedFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (utmSourceFilter) params.utm_source = utmSourceFilter;
      if (linkedFilter) params.linked = linkedFilter;
      const r = await api.get("/platform/intakes", { params });
      setData(r.data);
    } catch (e) {
      console.warn("Failed to load intakes:", e?.response?.data || e?.message);
    } finally {
      setLoading(false);
    }
  }, [search, utmSourceFilter, linkedFilter]);

  // Only auto-load on mount — subsequent loads are user-triggered via Apply/Refresh.
  // (Search + filter fields are typed freely; live-refetch would be jarring.)
  useEffect(() => { load(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  const fmt = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Link to="/platform" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground" data-testid="platform-intakes-back">
              <ArrowLeft className="mr-1 h-3 w-3" /> Platform console
            </Link>
            <h1 className="mt-2 font-heading text-3xl font-bold" style={{ color: "hsl(var(--haruki-leaf))" }}>
              Trial intakes
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Lead-capture records from <code>/trial</code>. No PHI. Linked intakes have completed sign-in.
            </p>
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="platform-intakes-refresh">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>

        {/* KPI counters — computed on the full intake population (not the current filter cohort) */}
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Card data-testid="platform-intakes-kpi-total">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-md bg-muted/50 p-2 text-muted-foreground">
                <Users className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Total intakes</div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-bold" data-testid="platform-intakes-kpi-total-value">
                    {data.overall_total.toLocaleString()}
                  </span>
                  <span className="text-xs text-muted-foreground">all time</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="platform-intakes-kpi-conversion">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-md p-2" style={{ background: "hsl(var(--haruki-leaf) / 0.15)", color: "hsl(var(--haruki-leaf))" }}>
                <TrendingUp className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Linked conversion</div>
                <div className="mt-0.5 flex items-baseline gap-2">
                  <span className="font-heading text-2xl font-bold" data-testid="platform-intakes-kpi-conversion-value">
                    {data.overall_linked.toLocaleString()}
                    <span className="text-base font-medium text-muted-foreground"> / {data.overall_total.toLocaleString()}</span>
                  </span>
                  <span className="text-xs font-semibold" style={{ color: "hsl(var(--haruki-leaf))" }} data-testid="platform-intakes-kpi-conversion-pct">
                    {data.overall_total > 0
                      ? `${((data.overall_linked / data.overall_total) * 100).toFixed(1)}%`
                      : "—"}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="flex flex-wrap items-end gap-3 p-4">
            <div className="min-w-[240px] flex-1">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Search</label>
              <div className="relative mt-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  data-testid="platform-intakes-search"
                  className="pl-9"
                  placeholder="Email or agency name…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && load()}
                />
              </div>
            </div>
            <div className="min-w-[160px]">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">UTM source</label>
              <Input
                data-testid="platform-intakes-utm-filter"
                className="mt-1"
                placeholder="Exact match…"
                value={utmSourceFilter}
                onChange={(e) => setUtmSourceFilter(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
              />
            </div>
            <div className="min-w-[140px]">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Status</label>
              <select
                data-testid="platform-intakes-status-filter"
                className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={linkedFilter}
                onChange={(e) => setLinkedFilter(e.target.value)}
              >
                <option value="">All</option>
                <option value="true">Linked (converted)</option>
                <option value="false">Pending</option>
              </select>
            </div>
            <Button onClick={load} data-testid="platform-intakes-apply" style={{ background: "hsl(var(--haruki-leaf))", color: "white" }}>
              Apply
            </Button>
          </CardContent>
        </Card>

        {/* UTM source breakdown */}
        {data.by_utm_source.length > 0 && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Breakdown by UTM source ({data.total_matching} matching)
              </div>
              <div className="flex flex-wrap gap-2" data-testid="platform-intakes-utm-breakdown">
                {data.by_utm_source.map((row, idx) => (
                  <Badge
                    key={idx} variant="outline"
                    className="cursor-pointer"
                    onClick={() => { setUtmSourceFilter(row.utm_source || ""); load(); }}
                  >
                    <Tag className="mr-1 h-3 w-3" />
                    {row.utm_source || "(no source)"}: <strong className="ml-1">{row.count}</strong>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="platform-intakes-table">
                <thead className="bg-muted/40">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2.5">Created</th>
                    <th className="px-4 py-2.5">Email / Name</th>
                    <th className="px-4 py-2.5">Agency</th>
                    <th className="px-4 py-2.5">Location</th>
                    <th className="px-4 py-2.5">Discipline</th>
                    <th className="px-4 py-2.5">UTM</th>
                    <th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr><td colSpan={7} className="p-8 text-center text-xs text-muted-foreground">Loading…</td></tr>
                  )}
                  {!loading && data.items.length === 0 && (
                    <tr><td colSpan={7} className="p-8 text-center text-xs text-muted-foreground">No intakes match the current filters.</td></tr>
                  )}
                  {!loading && data.items.map((it, idx) => {
                    const linked = !!it.linked_user_id;
                    const fullName = [it.first_name, it.last_name].filter(Boolean).join(" ");
                    return (
                      <tr key={it.intake_id || idx} className="border-t border-border hover:bg-muted/20" data-testid="platform-intakes-row">
                        <td className="px-4 py-2.5 align-top whitespace-nowrap text-xs text-muted-foreground">{fmt(it.created_at)}</td>
                        <td className="px-4 py-2.5 align-top">
                          <div className="flex items-center font-medium">
                            <Mail className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
                            <span className="break-all">{it.email}</span>
                          </div>
                          {fullName && <div className="mt-0.5 text-xs text-muted-foreground">{fullName}</div>}
                        </td>
                        <td className="px-4 py-2.5 align-top">{it.agency_name || "—"}</td>
                        <td className="px-4 py-2.5 align-top whitespace-nowrap">
                          <span className="inline-flex items-center text-xs">
                            <MapPin className="mr-1 h-3 w-3 text-muted-foreground" />
                            {[it.city, it.state].filter(Boolean).join(", ") || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 align-top whitespace-nowrap text-xs">{it.vertical || "—"}</td>
                        <td className="px-4 py-2.5 align-top">
                          {it.utm_source || it.utm_medium || it.utm_campaign ? (
                            <div className="space-y-0.5 text-xs">
                              {it.utm_source && <div><span className="text-muted-foreground">source:</span> <strong>{it.utm_source}</strong></div>}
                              {it.utm_medium && <div><span className="text-muted-foreground">medium:</span> {it.utm_medium}</div>}
                              {it.utm_campaign && <div><span className="text-muted-foreground">campaign:</span> {it.utm_campaign}</div>}
                            </div>
                          ) : <span className="text-xs text-muted-foreground">—</span>}
                        </td>
                        <td className="px-4 py-2.5 align-top whitespace-nowrap">
                          {linked ? (
                            <Badge variant="outline" className="border-green-700/40 text-green-400">
                              <CheckCircle2 className="mr-1 h-3 w-3" /> Linked
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-muted-foreground">
                              <Clock className="mr-1 h-3 w-3" /> Pending
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
              Showing {data.items.length} of {data.total_matching} matching intake(s). Max page size 500.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
